import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrRewardLogEntry } from '@/lib/api';

interface UseRewardHistoryReturn {
  entries: GrRewardLogEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useRewardHistory(): UseRewardHistoryReturn {
  const [entries, setEntries] = useState<GrRewardLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchHistory = useCallback(async (newOffset: number = 0, append: boolean = false) => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsHistory({ limit, offset: newOffset });
      if (fetchIdRef.current !== id) return;
      if (append) {
        setEntries((prev) => [...prev, ...result.entries]);
      } else {
        setEntries(result.entries);
      }
      setTotal(result.total);
      setOffset(newOffset);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load reward history');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchHistory(0, false);
  }, [fetchHistory]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchHistory(offset + limit, true);
    }
  }, [fetchHistory, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    entries,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
